import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  getRowImage, getBuffPrice, findSkinImage, buildStickerIndex, withStickerImages,
} from './helpers';
import StickerSlots from './StickerSlots';
import {
  isLendable, rowLocation, canUnwindTrade, untrackedFromHistory,
  matchPeople, rowPartnerName, involvesPerson, LOCATION,
} from './lending';
import './ModalTheme.css';
import './LendModal.css';

// "Which item are you lending?"
//
// This lists TRACKER ROWS, not inventory. That distinction is the whole design:
// the first thing anyone does with a lending page is write down loans that
// already happened, and by definition those items are not in their inventory
// any more. A picker that only offered what you currently hold would be unable
// to record the exact case it exists for.
//
// So every row is shown, tagged with where its item actually is, and the ones
// that already left are sorted to the top.

// Two sources that are not rows at all: items known only from Steam's trade
// history, split by which way they moved. SENT is the one that matters for
// lending — you handed it over and it has not come back — so it leads.
const SENT     = 'hist-sent';
const RECEIVED = 'hist-received';
const isHistory = f => f === SENT || f === RECEIVED;

const FILTERS = [
  { key: 'all',           label: 'All' },
  { key: SENT,            label: 'Sent to someone' },
  { key: LOCATION.GONE,   label: 'Not in your inventory' },
  { key: LOCATION.HELD,   label: 'Still held' },
  { key: LOCATION.TRADED, label: 'Marked traded away' },
  { key: RECEIVED,        label: 'Received, never tracked' },
];

const TAGS = {
  [LOCATION.HELD]:   { text: 'in your inventory', cls: 'held' },
  [LOCATION.GONE]:   { text: 'not in your inventory', cls: 'gone' },
  [LOCATION.TRADED]: { text: 'marked traded away', cls: 'traded' },
};

export default function LendPicker({
  rows = [], skinsData = [], livePrices, inventoryMap, syncedInventory = [],
  tradeHistory = [], variant = 'classic', onPick, onClose,
}) {
  const [query, setQuery]   = useState('');
  const [filter, setFilter] = useState('all');
  const [sel, setSel]       = useState(new Set());   // row indexes
  const [selNew, setSelNew] = useState(new Map());   // candidate key -> candidate
  const panelRef = useRef(null);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const esc = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', esc);
    return () => { document.body.style.overflow = ''; document.removeEventListener('keydown', esc); };
  }, [onClose]);

  const invIds = useMemo(
    () => new Set((syncedInventory || []).map(i => i.assetId).filter(Boolean)),
    [syncedInventory],
  );
  const hasInventory = invIds.size > 0;

  // History candidates carry sticker names but no pictures. Worth showing here
  // and not only on the cards: two of the same skin are told apart by what is
  // stuck to them, and this is the screen where you choose between them.
  const stickerIndex = useMemo(() => buildStickerIndex(skinsData), [skinsData]);

  // Typing a person's name switches the picker into "everything involving them".
  //
  // Asked for directly: searching "proklaim" should surface what you sent him
  // AND what he sent you, not just whichever direction the current filter
  // happens to be showing. Only kicks in on the default view, so the specific
  // filters keep meaning exactly what their labels say.
  const people = useMemo(
    () => matchPeople(query, { rows, tradeHistory }),
    [query, rows, tradeHistory],
  );
  const personNames = useMemo(() => people.map(p => p.name), [people]);
  const personMode = filter === 'all' && people.length > 0;

  const candidates = useMemo(() => {
    const q = query.trim().toLowerCase();
    // Traded-away rows are a recovery path, not a normal one, so they only ever
    // appear when asked for by name.
    const wantTraded = filter === LOCATION.TRADED;

    const list = (rows || [])
      .map((row, realIndex) => ({ row, realIndex }))
      .filter(({ row }) => wantTraded
        ? row.skin?.value && row.tradeId && row.tradedFor?.length > 0 && !(parseFloat(row.sellPrice) > 0) && !row.lend
        : isLendable(row))
      .map(e => ({ ...e, where: rowLocation(e.row, invIds, hasInventory) }))
      .filter(e => filter === 'all' || e.where === filter)
      // The query matches an item name OR the person attached to it, so one box
      // answers both "where is my Karambit" and "what does Rahfee have".
      .filter(({ row }) => !q
        || row.skin.value.toLowerCase().includes(q)
        || (rowPartnerName(row) || '').toLowerCase().includes(q));

    // Items that already left come first: they are what someone opening this
    // picker is most likely hunting for.
    const rank = { [LOCATION.GONE]: 0, [LOCATION.TRADED]: 1, [LOCATION.HELD]: 2 };
    return list.sort((a, b) =>
      (rank[a.where] - rank[b.where]) ||
      ((parseFloat(b.row.buyPrice) || 0) - (parseFloat(a.row.buyPrice) || 0)));
  }, [rows, query, filter, invIds, hasInventory]);

  // Items that exist only in the Steam trade history. Built once and filtered
  // by the same search box, so the two sources behave alike.
  const untracked = useMemo(() => {
    const q = query.trim().toLowerCase();

    // Person mode pulls BOTH directions, which is the whole point: what you
    // sent them and what they sent you, side by side.
    if (personMode) {
      return ['sent', 'received']
        .flatMap(direction => untrackedFromHistory({ rows, tradeHistory, direction }))
        .filter(c => involvesPerson(c.partner?.name, personNames))
        .sort((a, b) => new Date(b.at || 0) - new Date(a.at || 0));
    }

    if (!isHistory(filter)) return [];
    return untrackedFromHistory({
      rows, tradeHistory, direction: filter === SENT ? 'sent' : 'received',
    }).filter(c => !q
      || c.name.toLowerCase().includes(q)
      || (c.partner?.name || '').toLowerCase().includes(q));
  }, [filter, query, rows, tradeHistory, personMode, personNames]);

  const sentTotal = useMemo(
    () => untrackedFromHistory({ rows, tradeHistory, direction: 'sent' }).length,
    [rows, tradeHistory],
  );

  const goneCount = useMemo(
    () => (rows || []).filter(r => isLendable(r) && rowLocation(r, invIds, hasInventory) === LOCATION.GONE).length,
    [rows, invIds, hasInventory],
  );

  // Unwinding a trade deletes the rows it created, and that is refused outright
  // once any of them has been sold. Worked out up front so the warning and the
  // disabled button agree with each other.
  const unwinds = useMemo(() => [...sel]
    .map(i => ({ i, row: (rows || [])[i] }))
    .filter(({ row }) => row?.tradeId && row.tradedFor?.length > 0)
    .map(({ i, row }) => ({ i, row, ...canUnwindTrade(rows, row) })),
    [sel, rows]);

  const blocked = unwinds.filter(u => !u.ok);
  const willDrop = unwinds.reduce((n, u) => n + (u.ok ? u.received.length : 0), 0);

  const toggle = idx => setSel(prev => {
    const next = new Set(prev);
    if (next.has(idx)) next.delete(idx); else next.add(idx);
    return next;
  });

  const toggleNew = cand => setSelNew(prev => {
    const next = new Map(prev);
    if (next.has(cand.key)) next.delete(cand.key); else next.set(cand.key, cand);
    return next;
  });

  const totalPicked = sel.size + selNew.size;

  // Which of the two lists the grid renders. Person mode is the only view that
  // shows both, because "everything involving them" spans rows and history.
  const showRows      = personMode || !isHistory(filter);
  const showUntracked = personMode || isHistory(filter);

  return (
    <div className={`mdl-overlay ${variant}`} onMouseDown={e => { if (!panelRef.current?.contains(e.target)) onClose(); }}>
      <div className="mdl-panel wide lnd-picker" ref={panelRef}>
        <div className="mdl-head">
          <div className="mdl-head-txt">
            <h3 className="mdl-title lnd-big-title">Pick what is on loan</h3>
            <p className="mdl-sub">
              Anything you still own, whether or not it is in your inventory right now.
              {goneCount > 0 ? ` ${goneCount} of your items have already left.` : ''}
              {sentTotal > 0 ? ` "Sent to someone" has ${sentTotal} untracked items you handed over and never got back.` : ''}
            </p>
          </div>
          <button type="button" className="mdl-x" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="mdl-body">
          <input className="mdl-input" placeholder="Search your items…"
            value={query} onChange={e => setQuery(e.target.value)} />

          <div className="lnd-pick-filters">
            {FILTERS.map(f => (
              <button type="button" key={f.key}
                className={`mdl-btn sm ${filter === f.key ? 'primary' : ''}`}
                // Selections survive a filter change. Switching view to find the
                // second item of a pair should not silently drop the first, and
                // the footer count is there to say what is still held.
                onClick={() => setFilter(f.key)}>
                {f.label}
              </button>
            ))}
          </div>

          {personMode && (
            <div className="mdl-card">
              <div className="mdl-card-title">
                Everything involving {people.map(p => p.name).join(', ')}
              </div>
              <div className="mdl-card-hint">
                Both directions: what you sent them and what they sent you, plus any tracked row
                that names them. Sorted newest first. Search an item name instead to go back to
                the normal list.
              </div>
            </div>
          )}

          {!personMode && filter === SENT && (
            <div className="mdl-card">
              <div className="mdl-card-title">Items you handed over and never got back</div>
              <div className="mdl-card-hint">
                Straight from your Steam trade history, so it reaches back further than your rows do.
                Steam already recorded who you sent each one to, so <strong>the borrower is filled in
                for you</strong> — the next screen only asks for a price, because Steam records what
                moved and never what it cost.
              </div>
            </div>
          )}

          {!personMode && filter === RECEIVED && (
            <div className="mdl-card">
              <div className="mdl-card-title">Received, but never tracked</div>
              <div className="mdl-card-hint">
                Items sent TO you that have no row. Use this to lend out something you own but never
                added. The name shown is who gave it to you, which is not the borrower, so you will
                be asked who has it now.
              </div>
            </div>
          )}

          {filter === LOCATION.TRADED && (
            <div className="mdl-card warn">
              <div className="mdl-card-title">These are recorded as trades</div>
              <div className="mdl-card-hint">
                Picking one says the trade never happened. The items it says you received are
                deleted and their share of the cost basis goes back to this row. Use it when a
                lend was resolved as a trade by mistake. Anything you have since sold cannot be
                unwound.
              </div>
            </div>
          )}

          <div className="lnd-pick-grid">
            {/* Person mode is the one view that shows both lists at once. */}
            {showUntracked && untracked.length === 0 && !showRows && (
              <span className="lnd-none">
                {query ? 'Nothing matches that.'
                  : tradeHistory.length === 0
                    ? 'No trade history synced yet. Run a sync from the extension first.'
                    : filter === SENT
                      ? 'Everything you have sent already has a row.'
                      : 'Everything you have received already has a row.'}
              </span>
            )}
            {showUntracked && untracked.length === 0 && showRows && candidates.length === 0 && (
              <span className="lnd-none">Nothing involving that person.</span>
            )}
            {showUntracked && untracked.slice(0, 120).map(c => {
              const on = selNew.has(c.key);
              // No row means no stored icon, so the skins database is the only
              // picture available. Newer items simply show blank.
              const img = findSkinImage(skinsData, c.name, c.condition);
              const sent = c.direction === 'sent';
              return (
                <button type="button" key={c.key}
                  className={`lnd-pick${on ? ' on' : ''}`} onClick={() => toggleNew(c)}>
                  {img ? <img src={img} alt="" /> : <span className="lnd-item-blank" />}
                  <span className="lnd-item-txt">
                    <span className="lnd-item-name">{c.name}</span>
                    <span className="lnd-item-sub">
                      {c.condition || 'n/a'}
                      {c.at ? ` · ${new Date(c.at).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}` : ''}
                      {c.partner ? ` · ${sent ? 'sent to' : 'from'} ${c.partner.name}` : ''}
                    </span>
                    <StickerSlots
                      stickers={withStickerImages(c.stickers, stickerIndex)}
                      charms={c.charms}
                      prices={livePrices} variant={variant} max={5}
                      style={{ marginTop: 4 }} />
                    <span className={`lnd-where ${sent ? 'sent' : 'new'}`}>
                      {sent ? 'never came back' : 'never tracked'}
                    </span>
                  </span>
                </button>
              );
            })}

            {showRows && !showUntracked && candidates.length === 0 && (
              <span className="lnd-none">
                {query ? 'Nothing matches that.'
                  : filter === LOCATION.TRADED ? 'No traded-away items to recover.'
                  : filter === LOCATION.GONE ? 'Every item you track is still in your inventory.'
                  : 'You have no items to lend.'}
              </span>
            )}
            {showRows && candidates.slice(0, 120).map(({ row, realIndex, where }) => {
              const img = getRowImage(row, skinsData, inventoryMap);
              const mkt = getBuffPrice(livePrices, row);
              const on  = sel.has(realIndex);
              const tag = TAGS[where];
              // Only meaningful once an inventory has actually been synced, and
              // never worth saying about something you obviously still hold.
              const showTag = where !== LOCATION.HELD || filter === 'all';
              return (
                <button type="button" key={realIndex}
                  className={`lnd-pick${on ? ' on' : ''}`} onClick={() => toggle(realIndex)}>
                  {img ? <img src={img} alt="" /> : <span className="lnd-item-blank" />}
                  <span className="lnd-item-txt">
                    <span className="lnd-item-name">{row.skin.value}</span>
                    <span className="lnd-item-sub">
                      {row.condition?.value || 'n/a'} · {mkt != null ? `$${mkt.toFixed(2)}` : `paid $${(parseFloat(row.buyPrice) || 0).toFixed(2)}`}
                    </span>
                    <StickerSlots
                      stickers={withStickerImages(row.stickers, stickerIndex)}
                      charms={row.charms}
                      prices={livePrices} variant={variant} max={5}
                      style={{ marginTop: 4 }} />
                    {showTag && hasInventory && (
                      <span className={`lnd-where ${tag.cls}`}>{tag.text}</span>
                    )}
                  </span>
                </button>
              );
            })}
          </div>
          {showRows && candidates.length > 120 && (
            <span className="lnd-none">Showing the 120 most valuable. Search to narrow it down.</span>
          )}
          {showUntracked && untracked.length > 120 && (
            <span className="lnd-none">Showing the 120 most recent arrivals. Search to narrow it down.</span>
          )}

          {blocked.length > 0 && (
            <div className="mdl-card warn">
              <div className="mdl-card-title">Cannot unwind {blocked.length === 1 ? 'one of these' : `${blocked.length} of these`}</div>
              <div className="mdl-card-hint">
                {blocked.map(u => u.row.skin?.value).join(', ')} came with items you have since sold
                ({blocked.flatMap(u => u.sold.map(s => s.row.skin?.value)).join(', ')}).
                Deleting those would erase profit you actually made. Unpick it to continue.
              </div>
            </div>
          )}
        </div>

        <div className="mdl-foot">
          <span className="mdl-foot-note">
            {totalPicked} selected
            {selNew.size > 0 ? ` · ${selNew.size} new row${selNew.size !== 1 ? 's' : ''} will be created` : ''}
            {willDrop > 0 ? ` · ${willDrop} received item${willDrop !== 1 ? 's' : ''} will be removed` : ''}
          </span>
          <button type="button" className="mdl-btn" onClick={onClose}>Cancel</button>
          <button type="button" className="mdl-btn primary"
            disabled={totalPicked === 0 || blocked.length > 0}
            onClick={() => onPick({ indexes: [...sel], newItems: [...selNew.values()] })}>
            Continue
          </button>
        </div>
      </div>
    </div>
  );
}
